import { CascadeDialog } from '@/components/admin/versioned/cascade-dialog';
import {
  PointerDialog,
  type PointerDialogProps,
} from '@/components/admin/versioned/pointer-dialog';
import { useProdLinked } from '@/lib/promotion-api';
import type { PromotionGesture } from '@matter_extractor/shared';

/**
 * The confirmation of a move of a pointer, whichever box the server calls for (vitrine.md §4.1,
 * issue #192).
 *
 * - **« latest prod » with a prod linked**: the box of the cascade, which judges the gesture on
 *   the prod before the click and writes nothing here unless the prod applied. The gesture is a
 *   passage when the version is ahead of « latest prod », and the move back (`pointer`) when it is
 *   behind it.
 * - **Everything else** - « latest dev », and « latest prod » where no prod is linked (a
 *   workstation, the CI): the plain confirmation of a pointer move, which in the second case says
 *   that the passage stays local.
 *
 * It asks whether a prod is linked only when it is about to draw the box of a passage, and draws
 * the plain confirmation while the answer is awaited: a screen never waits on a question to show
 * a box, and a prod linked after all refuses the plain confirmation (`promotion-required`) instead
 * of letting a passage skip it.
 */

export interface PassageBoxProps extends PointerDialogProps {
  entityId: string;
  /** The passage went through on the prod and here: the caller re-reads what the cascade moved. */
  onPassed: () => void;
}

export function PassageBox({ entityId, onPassed, ...dialog }: PassageBoxProps) {
  const production = dialog.tag === 'production';
  const linked = useProdLinked(production);

  if (production && linked.data === true) {
    const aim = { kind: dialog.wording.kind, entityId, versionNumber: dialog.target.number };
    const gesture: Extract<PromotionGesture, { type: 'promote' | 'pointer' }> =
      dialog.current !== null && dialog.target.number < dialog.current.number
        ? { type: 'pointer', ...aim }
        : { type: 'promote', ...aim };
    return (
      <CascadeDialog
        wording={dialog.wording}
        name={dialog.name}
        gesture={gesture}
        target={dialog.target}
        current={dialog.current}
        onPassed={onPassed}
        onCancel={dialog.onCancel}
      />
    );
  }

  return <PointerDialog {...dialog} noProd={production && !linked.isPending} />;
}
